import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, ChevronDown, Shuffle } from "lucide-react";
import { api, errorMessage, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SURVEY_SECTIONS } from "@/constants/survey";
import { SurveyCharts } from "@/components/SurveyCharts";

const QUESTIONS = SURVEY_SECTIONS.flatMap((s) => s.questions);
const label = (id) => QUESTIONS.find((q) => q.id === id)?.label || id.replace(/_/g, " ");
const fmt = (v) => (Array.isArray(v) ? v.join("; ") : v === undefined || v === null ? "" : String(v));

export default function SurveyAdmin() {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [winner, setWinner] = useState(null);
  const load = () => api.get("/admin/survey").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete the response from ${r.email}?`)) return;
    try { await api.delete(`/admin/survey/${r.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const exportCsv = () => {
    const keys = [...new Set([...QUESTIONS.map((q) => q.id), ...rows.flatMap((r) => Object.keys(r.answers))])];
    const esc = (v) => `"${fmt(v).replace(/"/g, '""')}"`;
    const lines = [["submitted_at", "email", ...keys].map(esc).join(","), ...rows.map((r) => [r.created_at, r.email, ...keys.map((k) => r.answers[k])].map(esc).join(","))];
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `listener-survey-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click(); URL.revokeObjectURL(url);
  };
  const avg = (k) => { const v = rows.map((r) => Number(r.answers[k])).filter((n) => !Number.isNaN(n)); return v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1) : "—"; };

  if (!rows) return null;
  return (
    <div data-testid="admin-survey">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Listeners</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Survey responses</h1>
          <p className="mt-2 text-sm text-slate-400">{rows.length} response{rows.length === 1 ? "" : "s"} · avg satisfaction {avg("satisfaction")}/10 · avg recommend {avg("nps")}/10. Toggle the popup under Homepage.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setWinner(rows[Math.floor(Math.random() * rows.length)])} disabled={!rows.length} variant="outline" className="rounded-full border-white/15 bg-transparent hover:bg-white/10" data-testid="survey-draw-winner"><Shuffle size={15} className="mr-2" /> Draw a winner</Button>
          <Button onClick={exportCsv} disabled={!rows.length} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="survey-export-csv"><Download size={15} className="mr-2" /> Export CSV</Button>
        </div>
      </div>
      {winner && <div className="mb-6 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-4 text-sm" data-testid="survey-winner">Random draw: <strong>{winner.email}</strong> (submitted {formatDate(winner.created_at)})</div>}
      <SurveyCharts rows={rows} />
      <p className="eyebrow mb-3">Individual responses</p>
      <div className="space-y-2" data-testid="survey-list">
        {rows.length === 0 && <p className="text-sm text-slate-400" data-testid="survey-empty">No responses yet.</p>}
        {rows.map((r, i) => (
          <div key={r.id} className="card-surface rounded-2xl" data-testid={`survey-row-${i}`}>
            <div className="flex items-center gap-3 p-4">
              <button onClick={() => setOpenId(openId === r.id ? null : r.id)} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid={`survey-row-toggle-${i}`}>
                <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform ${openId === r.id ? "rotate-180" : ""}`} />
                <div className="min-w-0">
                  <p className="font-semibold truncate">{r.email}</p>
                  <p className="text-xs text-slate-400 truncate">{formatDate(r.created_at)} · most listened: {fmt(r.answers.show_most) || "—"} · satisfaction {fmt(r.answers.satisfaction) || "—"} · recommend {fmt(r.answers.nps) || "—"}</p>
                </div>
              </button>
              <button onClick={() => remove(r)} className="p-2 text-slate-400 hover:text-rose-400" data-testid={`survey-delete-${i}`}><Trash2 size={16} /></button>
            </div>
            {openId === r.id && (
              <dl className="border-t border-white/10 px-4 py-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid={`survey-row-detail-${i}`}>
                {Object.entries(r.answers).map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{label(k)}</dt><dd className="text-slate-200">{fmt(v) || "—"}</dd></div>)}
              </dl>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
